import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { ThrottleClickDirective } from '@directives/throttle-click.directive';
import { Idea } from '@models/Sessions/Idea/Idea';
import { VideoProject } from '@models/Sessions/Idea/VideoProject';
import { Session } from '@models/Sessions/Session';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { finalize, tap } from 'rxjs';

import { VideoPreviewComponent } from './video-preview/video-preview.component';
import {
  createControl,
  emptySceneControl,
  mapFormValueToData,
  reCreateControl,
  VideoProjectFormControl,
  VideoProjectFormService,
  VideoSceneFormControl,
} from './video-project.form';
import { VideoProjectService } from './video-project.service';
import { mapVideoClips } from './video-project.utils';
import { VideoSceneComponent } from './video-scene/video-scene.component';

@Component({
  selector: 'app-video-project',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ThrottleClickDirective,
    VideoSceneComponent,
  ],
  providers: [DialogService],
  templateUrl: './video-project.component.html',
  styleUrl: './video-project.component.scss',
})
export class VideoProjectComponent implements OnInit {
  @Input() session: Session;
  @Input() idea: Idea;

  form?: FormGroup<VideoProjectFormControl>;
  hasChanges = false;
  isLoading = false;
  isPreviewMode = false;

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly dialogService: DialogService,
    private readonly confirmationService: ConfirmationService,
    private readonly videoProjectService: VideoProjectService,
    private readonly videoProjectFormService: VideoProjectFormService,
  ) {}

  ngOnInit(): void {
    this.videoProjectService
      .get({ sessionId: this.session._id, ideaId: this.idea._id })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(({ videoProject }) => {
          this.create(videoProject);
        }),
        tap(() => {
          this.poll();
        }),
      )
      .subscribe();
  }

  private create(videoProject: VideoProject) {
    this.form = new FormGroup<VideoProjectFormControl>(
      createControl(videoProject),
    );
    this.videoProjectFormService
      .setInitialValue(this.form.value)
      .$onChanges(this.form)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([hasChanges]) => {
        this.hasChanges = hasChanges;
      });
  }

  private poll() {
    this.videoProjectService
      .polling({
        sessionId: this.session._id,
        ideaId: this.idea._id,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ videoProject }) => this.onAfterPoll(videoProject));
  }

  private onAfterPoll(videoProject: VideoProject) {
    const clips = mapVideoClips(videoProject);
    this.form.controls.scenes.controls
      .flatMap(scene => {
        return scene.controls.shots.controls.flatMap(shot => {
          return shot.controls.clips.controls;
        });
      })
      .forEach(control => {
        const clip = clips[control.value.id];
        if (!clip) {
          return;
        }
        if (clip.asset.state === control.value.asset.state) {
          return;
        }
        control.setControl('asset', new FormControl(clip.asset));
        this.videoProjectFormService.setClipInitialValue(control);
      });
  }

  onAssembleClick() {
    // eslint-disable-next-line no-console
    console.log(this.form.value);
  }

  onPreviewClick() {
    this.dialogService.open(VideoPreviewComponent, {
      header: 'Video Project Preview',
      modal: true,
      draggable: false,
      resizable: false,
      maximizable: false,
      closeOnEscape: true,
      dismissableMask: true,
      closable: true,
      styleClass: 'w-2/4',
      data: {
        scenes: this.form.controls.scenes,
      },
    });
  }

  onRestartClick(event: Event) {
    this.confirmationService.confirm({
      target: event.target,
      message: `
            This action will re-create Video Project from scratch! Click 'Cancel' if you changed your mind.
          `,
      icon: 'pi pi-exclamation-circle',
      acceptButtonProps: {
        icon: 'pi pi-check',
        label: 'Confirm',
      },
      rejectButtonProps: {
        icon: 'pi pi-times',
        label: 'Cancel',
        outlined: true,
      },
      accept: () => {
        this.restart();
      },
      reject: () => {
        /** noop */
      },
    });
  }

  private restart() {
    this.isLoading = true;
    this.videoProjectService
      .upsert({
        sessionId: this.session._id,
        ideaId: this.idea._id,
      })
      .pipe(
        tap(({ videoProject }) => {
          this.onAfterRestart(videoProject);
        }),
        tap(() => {
          this.videoProjectFormService.setInitialValue(this.form.value);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private onAfterRestart(videoProject: VideoProject) {
    const form = new FormGroup<VideoProjectFormControl>(
      createControl(videoProject),
    );
    for (const name of Object.getOwnPropertyNames(form.controls)) {
      this.form.setControl(name as any, form.get(name));
    }
  }

  onSaveClick() {
    this.isLoading = true;
    this.videoProjectService
      .update(
        {
          sessionId: this.session._id,
          ideaId: this.idea._id,
        },
        mapFormValueToData(this.form.value),
      )
      .pipe(
        tap(({ videoProject }) => {
          this.onAfterSave(videoProject);
        }),
        tap(() => {
          this.videoProjectFormService.setInitialValue(this.form.value);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private onAfterSave(videoProject: VideoProject) {
    const form = new FormGroup<VideoProjectFormControl>(
      createControl(videoProject),
    );
    for (const name of Object.getOwnPropertyNames(form.controls)) {
      this.form.setControl(name as any, form.get(name));
    }
  }

  onResetClick(event: Event) {
    this.confirmationService.confirm({
      target: event.target,
      message: `
          This will revert all unsaved changes! Click 'Cancel' if you changed your mind.
        `,
      icon: 'pi pi-exclamation-circle',
      acceptButtonProps: {
        icon: 'pi pi-check',
        label: 'Confirm',
      },
      rejectButtonProps: {
        icon: 'pi pi-times',
        label: 'Cancel',
        outlined: true,
      },
      accept: () => {
        this.reset();
      },
      reject: () => {
        /** noop */
      },
    });
  }

  private reset() {
    const initialValue = this.videoProjectFormService.getInitialValue();
    if (!initialValue) {
      return;
    }
    const form = new FormGroup<VideoProjectFormControl>(
      reCreateControl(initialValue),
    );
    for (const name of Object.getOwnPropertyNames(form.controls)) {
      this.form.setControl(name as any, form.get(name));
    }
  }

  onCreateClick() {
    this.isLoading = true;
    this.videoProjectService
      .upsert({ sessionId: this.session._id, ideaId: this.idea._id })
      .pipe(
        tap(({ videoProject }) => {
          this.create(videoProject);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  onSceneAddClick(scene?: FormGroup<VideoSceneFormControl>) {
    this.form.controls.scenes.insert(
      scene ? this.findSceneIndex(scene) + 1 : 0,
      new FormGroup<VideoSceneFormControl>(emptySceneControl()),
    );
  }

  onSceneDeleteClick(scene: FormGroup<VideoSceneFormControl>) {
    const index = this.findSceneIndex(scene);
    if (index >= 0) {
      this.form.controls.scenes.removeAt(index);
    }
  }

  private findSceneIndex(scene: FormGroup<VideoSceneFormControl>) {
    return this.form.controls.scenes.controls.indexOf(scene);
  }
}
